import { parse, stringify } from 'qs';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { historyPush } from 'reactApp/modules/router/router.module';
import { sendSearchInputClickRadar } from 'reactApp/modules/search/search.analytics';
import { getSearchQueryString } from 'reactApp/modules/search/search.helpers';
import { setQueryParams } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    EActions,
    ESearchOptionType,
    IAction,
    ISearchContent,
    ISearchContentProps,
    IState,
    typesOptions,
} from 'reactApp/ui/Header/Search/Search.data';

const reducer = (state: IState, action: IAction): IState => {
    switch (action.type) {
        case EActions.close:
            return {
                active: false,
                focus: false,
                value: '',
                type: 'all',
            };
        case EActions.removeType:
            return {
                ...state,
                type: 'all',
                focus: false,
            };
        case EActions.setFocus:
            return {
                ...state,
                focus: Boolean(action.payload),
            };
        case EActions.setType:
            return {
                ...state,
                type: String(action.payload),
            };
        case EActions.setActive:
            return {
                ...state,
                active: true,
                focus: true,
            };
        case EActions.setValue:
            return {
                ...state,
                value: String(action.payload),
            };
    }
};

// eslint-disable-next-line max-lines-per-function,sonarjs/cognitive-complexity
export const useSearchContent = ({
    toggleSearch,
    rootRef,
    isExpanded,
    onlyExpanded,
    storage,
    dropdownRef,
}: ISearchContentProps): ISearchContent => {
    const { isAttaches, isReactPage } = getStorage(storage);

    const { query = '', searchType = ESearchOptionType.all } = useSelector(SettingsSelectors.getQueryParams) || {};

    const [state, dispatch] = useReducer(reducer, {
        active: Boolean(onlyExpanded),
        focus: false,
        value: query,
        type: searchType,
    });

    const storeDispatch = useDispatch();

    const placeholder = isAttaches ? 'Поиск по разделу' : 'Поиск по Облаку';
    const searchOption = isAttaches ? undefined : typesOptions[state.type];

    const showSearchPage = useCallback(() => {
        const { value, type } = state;

        if (!value && type === 'all') {
            return;
        }

        const { query: _query, searchType: _, ...rest } = parse(window.location.search, { ignoreQueryPrefix: true });
        const searchOther = stringify(rest);

        const search = getSearchQueryString(value, type);

        if (isAttaches) {
            storeDispatch(historyPush({ id: '/attaches', search: `${search}${searchOther ? `&${searchOther}` : ''}` }));
            return;
        }

        if (isReactPage) {
            // setQueryParams вызываем чтобы компонент SearchPage сразу получал query (иначе сначала query=undefined только потом в стор проставляется значение)
            const searchRequestParams = parse(search, { ignoreQueryPrefix: true });

            storeDispatch(setQueryParams(searchRequestParams));
            storeDispatch(historyPush({ id: '/search/', search }));
        }
    }, [isAttaches, isReactPage, state]);

    const onInputClick = useCallback(() => {
        if (isExpanded || onlyExpanded) {
            dispatch({ type: EActions.setFocus, payload: true });
        } else {
            toggleSearch(true);
            sendSearchInputClickRadar({ storage });
        }
    }, [isExpanded, onlyExpanded, toggleSearch]);

    const onSearchClick = useCallback(() => {
        if (state.active) {
            dispatch({ type: EActions.setFocus, payload: false });
            showSearchPage();
            return;
        }

        toggleSearch(true);
    }, [showSearchPage, state.active, toggleSearch]);

    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: EActions.setValue, payload: event.target?.value });
    }, []);

    const onDocumentClick = useCallback(
        (event: Event): any => {
            let closeIt = true;
            let target = event.target as HTMLElement;
            while (target && target.parentNode !== document) {
                if (target === rootRef?.current || target === dropdownRef?.current) {
                    closeIt = false;
                    break;
                }
                target = target.parentNode as HTMLElement;
            }

            if (!closeIt) {
                return;
            }

            if (onlyExpanded) {
                dispatch({ type: EActions.setFocus, payload: false });
                return;
            }
            toggleSearch(false);
        },
        [dropdownRef, onlyExpanded, rootRef, toggleSearch]
    );

    const setActiveInput = useCallback(() => {
        dispatch({ type: EActions.setActive });
    }, []);

    const chooseSearchType = useCallback((type: string) => {
        if (typesOptions[type]) {
            dispatch({ type: EActions.setType, payload: type });
        }
    }, []);

    const removeSearchType = useCallback(() => {
        dispatch({ type: EActions.removeType });
    }, []);

    const removeFocus = useCallback(() => {
        dispatch({ type: EActions.setFocus, payload: false });
    }, []);

    const onKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                dispatch({ type: EActions.setFocus, payload: false });
                showSearchPage();
            }
        },
        [showSearchPage]
    );

    useEffect(() => {
        if (!isExpanded) {
            dispatch({ type: EActions.close });
        }
    }, [isExpanded]);

    return {
        isActive: state.active,
        isFocus: state.focus,
        value: state.value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onDocumentClick,
        onInputChange,
        onKeyUp,
        setActiveInput,
        chooseSearchType,
        removeSearchType,
        removeFocus,
    };
};
